/**
 * «Подарок» — ONE definition of a gift for the path, the teacher, move judgement and metrics
 * (docs/PATH-V2.md §12.3, docs/path-v2/CONTRACTS.md §3.1).
 *
 * - `gift(sq)`: the side to move has a legal capture on `sq`, and after (at least one such) capture
 *   the opponent has no legal recapture on the landing square.
 * - `isGift(move)`: the move is such a capture AND it is not poisoned: a pure, deterministic material
 *   search (captures, promotions and checks; check evasions in full; 4 plies after the capture;
 *   alpha-beta on 1/3/3/5/9; mate and king capture decisive) ends with the mover's material at least
 *   where it was before the capture (net change >= 0). "Not capturing" is the baseline 0: the
 *   position stays as it is. A free pawn that walks into a mate, loses the queen to a fork or
 *   uncovers an attack on the queen is poisoned and is not a gift.
 *
 * Legality. With a king on the board a side may never leave its own king attacked (exactly chess.js
 * legality for both kings present: pins, checks, the king captures only unguarded pieces, en
 * passant, promotions; castling is generated for the root move list only). A side without a king
 * moves pseudo-legally — the kingless path mini-games of §6.6. Stalemate / draw rules are never
 * consulted. The rules are implemented here on the `analysis/board.ts` geometry (no chess.js
 * `attackers()`, no `findHanging`) so that one search node costs microseconds; the test suite
 * checks the generated legal moves against chess.js on random positions.
 *
 * Pure and isomorphic. Squares in the API are algebraic (`e4`), moves are UCI (`e7d8q`).
 */
import type { Color, PieceType, Square } from '@gambit/shared';
import {
  ALL_DIRS,
  attacksFrom,
  attackersOf,
  defendersOf,
  directionBetween,
  fileOf,
  onBoard,
  opposite,
  parsePlacement,
  rankOf,
  squareIndex,
  squareName,
  toIndex,
  VALUE_PAWNS,
  type Board,
  type BoardPiece,
  type Direction,
} from './board.ts';

/** Plies searched after the capture by `isGift`. */
export const GIFT_SEARCH_PLIES = 4;
/** Quiet checks are tried only while at least this many plies remain (i.e. plies 1–3 after the capture). */
const CHECK_MIN_DEPTH = 2;
/** Decisive score (mate / king capture), far above any material swing. */
const MATE = 1000;
/** Pieces worth at least this much count for `ownHung`. */
const OWN_HUNG_MIN = 3;

const KNIGHT_JUMPS: readonly Direction[] = [
  [1, 2],
  [2, 1],
  [2, -1],
  [1, -2],
  [-1, -2],
  [-2, -1],
  [-2, 1],
  [-1, 2],
];
const PROMOTIONS: readonly PieceType[] = ['q', 'r', 'b', 'n'];

/** Mutable position for the generator and the search. */
interface Pos {
  board: Board;
  turn: Color;
  /** En passant target square index or -1. */
  ep: number;
  castling: string;
  kings: Record<Color, number>;
}

interface Move {
  from: number;
  to: number;
  /** Square of the captured piece (differs from `to` only for en passant), -1 for a quiet move. */
  capSq: number;
  captured: PieceType | null;
  promo: PieceType | null;
  double: boolean;
  /** Castling: rook from / to, else -1. */
  rookFrom: number;
  rookTo: number;
}

interface Undo {
  moving: BoardPiece;
  capPiece: BoardPiece | null;
  ep: number;
  kings: Record<Color, number>;
}

// ---------------------------------------------------------------------------------------------
// Position parsing and make / unmake
// ---------------------------------------------------------------------------------------------

function parseFen(fen: string): Pos {
  const board = parsePlacement(fen);
  const fields = fen.trim().split(/\s+/);
  const turn: Color = fields[1] === 'b' ? 'b' : 'w';
  const castling = fields[2] && fields[2] !== '-' ? fields[2] : '';
  const ep = fields[3] && fields[3] !== '-' ? squareIndex(fields[3]) : -1;
  const kings: Record<Color, number> = { w: -1, b: -1 };
  for (let i = 0; i < 64; i++) {
    const p = board[i];
    if (p && p.type === 'k' && kings[p.color] < 0) kings[p.color] = i;
  }
  return { board, turn, ep, castling, kings };
}

function make(pos: Pos, m: Move): Undo {
  const b = pos.board;
  const moving = b[m.from] as BoardPiece;
  const capPiece = m.capSq >= 0 ? (b[m.capSq] ?? null) : null;
  const undo: Undo = { moving, capPiece, ep: pos.ep, kings: { w: pos.kings.w, b: pos.kings.b } };
  if (m.capSq >= 0) b[m.capSq] = null;
  b[m.from] = null;
  b[m.to] = m.promo ? { type: m.promo, color: moving.color } : moving;
  if (m.rookFrom >= 0) {
    b[m.rookTo] = b[m.rookFrom] ?? null;
    b[m.rookFrom] = null;
  }
  if (moving.type === 'k') pos.kings[moving.color] = m.to;
  if (capPiece && capPiece.type === 'k') pos.kings[capPiece.color] = -1;
  pos.ep = m.double ? (m.from + m.to) >> 1 : -1;
  pos.turn = opposite(pos.turn);
  return undo;
}

function unmake(pos: Pos, m: Move, u: Undo): void {
  const b = pos.board;
  if (m.rookFrom >= 0) {
    b[m.rookFrom] = b[m.rookTo] ?? null;
    b[m.rookTo] = null;
  }
  b[m.to] = null;
  b[m.from] = u.moving;
  if (m.capSq >= 0) b[m.capSq] = u.capPiece;
  pos.ep = u.ep;
  pos.kings = u.kings;
  pos.turn = u.moving.color;
}

// ---------------------------------------------------------------------------------------------
// Attacks and move generation
// ---------------------------------------------------------------------------------------------

/** Is `target` attacked by any piece of `by` (pseudo-legal, early exit; same geometry as attackersOf). */
function isAttacked(b: Board, target: number, by: Color): boolean {
  const f0 = fileOf(target);
  const r0 = rankOf(target);
  const pr = by === 'w' ? r0 - 1 : r0 + 1;
  for (const df of [-1, 1]) {
    if (!onBoard(f0 + df, pr)) continue;
    const p = b[toIndex(f0 + df, pr)];
    if (p && p.color === by && p.type === 'p') return true;
  }
  for (const [df, dr] of KNIGHT_JUMPS) {
    if (!onBoard(f0 + df, r0 + dr)) continue;
    const p = b[toIndex(f0 + df, r0 + dr)];
    if (p && p.color === by && p.type === 'n') return true;
  }
  for (const [df, dr] of ALL_DIRS) {
    let f = f0 + df;
    let r = r0 + dr;
    let first = true;
    const diagonal = df !== 0 && dr !== 0;
    while (onBoard(f, r)) {
      const p = b[toIndex(f, r)];
      if (p) {
        if (p.color === by) {
          if (p.type === 'q' || (diagonal ? p.type === 'b' : p.type === 'r')) return true;
          if (first && p.type === 'k') return true;
        }
        break;
      }
      first = false;
      f += df;
      r += dr;
    }
  }
  return false;
}

function mv(from: number, to: number, b: Board, capSq = -1, promo: PieceType | null = null): Move {
  const victim = capSq >= 0 ? b[capSq] : b[to];
  const cs = capSq >= 0 ? capSq : victim ? to : -1;
  return { from, to, capSq: cs, captured: victim ? victim.type : null, promo, double: false, rookFrom: -1, rookTo: -1 };
}

/**
 * Pseudo-legal moves of the side to move. `promos`: every promotion piece (root lists) or the queen
 * only (search). Castling only with `castle` (it needs the attack test and is never a capture).
 */
function pseudoMoves(pos: Pos, promos: readonly PieceType[], castle: boolean): Move[] {
  const b = pos.board;
  const us = pos.turn;
  const out: Move[] = [];
  for (let from = 0; from < 64; from++) {
    const p = b[from];
    if (!p || p.color !== us) continue;
    const f0 = fileOf(from);
    const r0 = rankOf(from);
    if (p.type === 'p') {
      const dr = us === 'w' ? 1 : -1;
      const startRank = us === 'w' ? 1 : 6;
      const lastRank = us === 'w' ? 7 : 0;
      const r1 = r0 + dr;
      if (!onBoard(f0, r1)) continue;
      const pushTo = toIndex(f0, r1);
      if (!b[pushTo]) {
        if (r1 === lastRank) for (const pr of promos) out.push(mv(from, pushTo, b, -1, pr));
        else {
          out.push(mv(from, pushTo, b));
          const r2 = r1 + dr;
          if (r0 === startRank && onBoard(f0, r2) && !b[toIndex(f0, r2)]) {
            const m = mv(from, toIndex(f0, r2), b);
            m.double = true;
            out.push(m);
          }
        }
      }
      for (const df of [-1, 1]) {
        if (!onBoard(f0 + df, r1)) continue;
        const to = toIndex(f0 + df, r1);
        const victim = b[to];
        if (victim && victim.color !== us) {
          if (r1 === lastRank) for (const pr of promos) out.push(mv(from, to, b, -1, pr));
          else out.push(mv(from, to, b));
        } else if (!victim && to === pos.ep) {
          const capSq = toIndex(f0 + df, r0);
          const vp = b[capSq];
          if (vp && vp.type === 'p' && vp.color !== us) out.push(mv(from, to, b, capSq));
        }
      }
      continue;
    }
    for (const to of attacksFrom(b, from)) {
      const victim = b[to];
      if (!victim || victim.color !== us) out.push(mv(from, to, b));
    }
  }
  if (castle) pushCastling(pos, out);
  return out;
}

function pushCastling(pos: Pos, out: Move[]): void {
  const b = pos.board;
  const us = pos.turn;
  const them = opposite(us);
  const rank = us === 'w' ? 0 : 7;
  const e = toIndex(4, rank);
  const king = b[e];
  if (pos.kings[us] !== e || !king || king.type !== 'k') return;
  const rights = us === 'w' ? { k: 'K', q: 'Q' } : { k: 'k', q: 'q' };
  const sides: Array<[right: string, rookFile: number, empty: number[], safe: number[], kingTo: number, rookTo: number]> = [
    [rights.k, 7, [5, 6], [4, 5, 6], 6, 5],
    [rights.q, 0, [1, 2, 3], [4, 3, 2], 2, 3],
  ];
  for (const [right, rookFile, empty, safe, kingTo, rookTo] of sides) {
    if (!pos.castling.includes(right)) continue;
    const rook = b[toIndex(rookFile, rank)];
    if (!rook || rook.type !== 'r' || rook.color !== us) continue;
    if (empty.some((f) => b[toIndex(f, rank)])) continue;
    if (safe.some((f) => isAttacked(b, toIndex(f, rank), them))) continue;
    out.push({
      from: e,
      to: toIndex(kingTo, rank),
      capSq: -1,
      captured: null,
      promo: null,
      double: false,
      rookFrom: toIndex(rookFile, rank),
      rookTo: toIndex(rookTo, rank),
    });
  }
}

/** After `make`, did the side that just moved leave its own king attacked? */
function leftKingAttacked(pos: Pos): boolean {
  const mover = opposite(pos.turn);
  const k = pos.kings[mover];
  return k >= 0 && isAttacked(pos.board, k, pos.turn);
}

function inCheck(pos: Pos): boolean {
  const k = pos.kings[pos.turn];
  return k >= 0 && isAttacked(pos.board, k, opposite(pos.turn));
}

function isLegal(pos: Pos, m: Move): boolean {
  const u = make(pos, m);
  const ok = !leftKingAttacked(pos);
  unmake(pos, m, u);
  return ok;
}

function legalMoves(pos: Pos, promos: readonly PieceType[] = PROMOTIONS, castle = true): Move[] {
  return pseudoMoves(pos, promos, castle).filter((m) => isLegal(pos, m));
}

function legalCaptures(pos: Pos): Move[] {
  return pseudoMoves(pos, PROMOTIONS, false).filter((m) => m.capSq >= 0 && isLegal(pos, m));
}

/** Any legal capture landing on `sq` for the side to move. */
function hasLegalCaptureOn(pos: Pos, sq: number): boolean {
  const b = pos.board;
  const victim = b[sq];
  if (!victim || victim.color === pos.turn) return false;
  for (const from of attackersOf(b, sq, pos.turn)) {
    const p = b[from] as BoardPiece;
    const promo = p.type === 'p' && (rankOf(sq) === 7 || rankOf(sq) === 0) ? 'q' : null;
    if (isLegal(pos, mv(from, sq, b, -1, promo))) return true;
  }
  return false;
}

function legalCaptureFromsOn(pos: Pos, sq: number): number[] {
  const b = pos.board;
  const victim = b[sq];
  if (!victim || victim.color === pos.turn) return [];
  return attackersOf(b, sq, pos.turn).filter((from) => {
    const p = b[from] as BoardPiece;
    const promo = p.type === 'p' && (rankOf(sq) === 7 || rankOf(sq) === 0) ? 'q' : null;
    return isLegal(pos, mv(from, sq, b, -1, promo));
  });
}

function toUci(m: Move): string {
  return `${squareName(m.from)}${squareName(m.to)}${m.promo ?? ''}`;
}

function findMove(pos: Pos, uci: string): Move | null {
  const from = squareIndex(uci.slice(0, 2));
  const to = squareIndex(uci.slice(2, 4));
  if (from < 0 || to < 0) return null;
  const promo = uci.length > 4 ? uci.charAt(4).toLowerCase() : null;
  const candidates = pseudoMoves(pos, PROMOTIONS, true).filter((m) => m.from === from && m.to === to && isLegal(pos, m));
  if (candidates.length === 0) return null;
  if (!candidates[0]?.promo) return candidates[0] ?? null;
  return candidates.find((m) => m.promo === (promo ?? 'q')) ?? null;
}

// ---------------------------------------------------------------------------------------------
// Material search
// ---------------------------------------------------------------------------------------------

function gainOf(m: Move): number {
  let g = m.captured ? (m.captured === 'k' ? MATE : VALUE_PAWNS[m.captured]) : 0;
  if (m.promo) g += VALUE_PAWNS[m.promo] - 1;
  return g;
}

/** Cheap necessary condition for a quiet move to give check (the full test runs after `make`). */
function mayGiveCheck(b: Board, m: Move, king: number): boolean {
  const piece = b[m.from] as BoardPiece;
  if (directionBetween(king, m.from)) return true; // a discovered check is possible
  const df = Math.abs(fileOf(king) - fileOf(m.to));
  const dr = rankOf(king) - rankOf(m.to);
  switch (piece.type) {
    case 'p':
      return df === 1 && dr === (piece.color === 'w' ? 1 : -1);
    case 'n':
      return (df === 1 && Math.abs(dr) === 2) || (df === 2 && Math.abs(dr) === 1);
    case 'k':
      return false;
    case 'b':
      return df === Math.abs(dr) && df > 0;
    case 'r':
      return df === 0 || dr === 0;
    default:
      return df === 0 || dr === 0 || df === Math.abs(dr);
  }
}

const QUEEN_ONLY: readonly PieceType[] = ['q'];

/**
 * Negamax alpha-beta on material from the side to move's view: the best net material change it can
 * reach from here. Standing pat (0) is allowed unless in check; in check every evasion is searched
 * and no evasion is mate. Captures and promotions everywhere, quiet checks while `depth >= 2`.
 * `quiesce`: past the horizon (depth 0) captures and promotions go on (and check evasions in full)
 * until the line is quiet, so no line ends on a capture nobody may answer — `captureNet`; `isGift`
 * keeps the plain horizon.
 */
function search(pos: Pos, depth: number, alpha: number, beta: number, stats: { nodes: number }, quiesce = false): number {
  stats.nodes++;
  const checked = inCheck(pos);
  let best = checked ? -MATE : 0;
  if (!checked) {
    if (best >= beta) return best;
    if (best > alpha) alpha = best;
    if (depth === 0 && !quiesce) return best;
  } else if (depth === 0 && !quiesce) {
    return pseudoMoves(pos, QUEEN_ONLY, false).some((m) => isLegal(pos, m)) ? 0 : -MATE;
  }
  const them = opposite(pos.turn);
  const enemyKing = pos.kings[them];
  const quietChecks = !checked && depth >= CHECK_MIN_DEPTH && enemyKing >= 0;
  const b = pos.board;
  const scored: Array<[Move, number]> = [];
  for (const m of pseudoMoves(pos, QUEEN_ONLY, false)) {
    if (m.captured === 'k') return MATE; // only possible when the victim side moves pseudo-legally
    const tactical = m.capSq >= 0 || m.promo !== null;
    if (!checked && !tactical && !(quietChecks && mayGiveCheck(b, m, enemyKing))) continue;
    // MVV-LVA, quiet checks last
    const order = tactical ? 100 + gainOf(m) * 10 - VALUE_PAWNS[(b[m.from] as BoardPiece).type] : 0;
    scored.push([m, order]);
  }
  scored.sort((x, y) => y[1] - x[1]);
  let legal = false;
  for (const [m] of scored) {
    const tactical = m.capSq >= 0 || m.promo !== null;
    const u = make(pos, m);
    if (leftKingAttacked(pos)) {
      unmake(pos, m, u);
      continue;
    }
    if (!checked && !tactical) {
      const k = pos.kings[them];
      if (!(k >= 0 && isAttacked(pos.board, k, opposite(them)))) {
        unmake(pos, m, u);
        continue;
      }
    }
    legal = true;
    const g = gainOf(m);
    const score = g - search(pos, Math.max(0, depth - 1), g - beta, g - alpha, stats, quiesce);
    unmake(pos, m, u);
    if (score > best) {
      best = score;
      if (best > alpha) alpha = best;
      if (alpha >= beta) break;
    }
  }
  if (checked && !legal) return -MATE;
  return best;
}

/** Node counter of the last `isGift` searches (for tests / perf diagnostics). */
export const giftSearchStats = { nodes: 0 };

/** No legal recapture on the landing square after the (legal) capture `m`. */
function noRecapture(pos: Pos, m: Move): boolean {
  const u = make(pos, m);
  const free = !hasLegalCaptureOn(pos, m.to);
  unmake(pos, m, u);
  return free;
}

/** `m` must be a legal move of `pos`. */
function isGiftMove(pos: Pos, m: Move): boolean {
  if (m.capSq < 0 || !noRecapture(pos, m)) return false;
  const g = gainOf(m);
  const u = make(pos, m);
  // Null window around `g`: the capture is poisoned iff the opponent's best reply line exceeds g.
  const reply = search(pos, GIFT_SEARCH_PLIES, g, g + 1, giftSearchStats);
  unmake(pos, m, u);
  return g - reply >= 0;
}

/** Same placement, the other side to move, no en passant (a "what if the opponent moved now" view). */
function flipped(pos: Pos): Pos {
  return { board: pos.board.slice(), turn: opposite(pos.turn), ep: -1, castling: '', kings: { ...pos.kings } };
}

function clonePos(pos: Pos): Pos {
  return { board: pos.board.slice(), turn: pos.turn, ep: pos.ep, castling: pos.castling, kings: { ...pos.kings } };
}

/** Squares of pieces of the side NOT to move in `pos` that the side to move can win as an `isGift`. */
function giftTargets(pos: Pos, minValue: number): Set<number> {
  const out = new Set<number>();
  for (const m of legalCaptures(pos)) {
    const sq = m.capSq;
    if (out.has(sq) || VALUE_PAWNS[m.captured as PieceType] < minValue) continue;
    if (isGiftMove(pos, m)) out.add(sq);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------------------------

/**
 * Squares of opponent pieces the side to move can capture legally with no legal recapture on the
 * landing square (en passant: the square of the passed pawn). Sorted a1..h8. Not poison-checked —
 * see `isGift` / `giftMoves` for that.
 */
export function giftSquares(fen: string): Square[] {
  const pos = parseFen(fen);
  const out = new Set<number>();
  for (const m of legalCaptures(pos)) {
    const sq = m.capSq;
    if (!out.has(sq) && noRecapture(pos, m)) out.add(sq);
  }
  return [...out].sort((a, b) => a - b).map(squareName);
}

/** Is the piece on `sq` a gift for the side to move (some legal capture of it has no legal recapture)? */
export function gift(fen: string, sq: Square): boolean {
  const target = squareIndex(sq);
  if (target < 0) return false;
  const pos = parseFen(fen);
  return legalCaptures(pos).some((m) => m.capSq === target && noRecapture(pos, m));
}

/**
 * The capture `uci` is a gift AND not poisoned: after it there is no legal recapture on the landing
 * square, and a 4-ply material search (captures, promotions, checks, full check evasions; mate and
 * king capture decisive) shows no net material loss for the mover. False for an illegal or quiet move.
 * A promotion without a piece letter means the queen.
 */
export function isGift(fen: string, uci: string): boolean {
  const pos = parseFen(fen);
  const m = findMove(pos, uci);
  return m !== null && isGiftMove(pos, m);
}

/**
 * The net material of the capture `uci` for the mover, board-wide: the `isGift` search (captures, promotions, checks,
 * full check evasions; 4 plies after the capture) carried on with captures only past its horizon, so no line ends on
 * a capture nobody may answer. Chapter 1.4 «Размен» counts a trade on its square (path/checks.ts `tradeBalance`);
 * this is the check that nothing else on the board turns it. Null for an illegal or quiet move.
 */
export function captureNet(fen: string, uci: string): number | null {
  const pos = parseFen(fen);
  const m = findMove(pos, uci);
  if (m === null || m.capSq < 0) return null;
  const g = gainOf(m);
  make(pos, m);
  return g - search(pos, GIFT_SEARCH_PLIES, -2 * MATE, 2 * MATE, { nodes: 0 }, true);
}

/**
 * The net material of ANY legal move `uci` for the mover (pawns, 1/3/3/5/9), board-wide: what it takes, minus the
 * opponent's best answer in the `isGift` search (captures, promotions, quiet checks while 2 plies remain, full check
 * evasions; a mate decisive) carried on with captures past its horizon — `captureNet` for a quiet move too. So a
 * check that forks, a skewer by a check, a piece left (or put) en prise and a mate in one all count: −1000 or less =
 * the move walks into mate. Quiet threats (a pin, a fork without check) stay unseen. Null for an illegal move.
 * The generator's gate (core path/gen/gate.ts, fixer r1 of round 2): no accepted answer may lose material by it.
 */
export function moveValue(fen: string, uci: string): number | null {
  const pos = parseFen(fen);
  const m = findMove(pos, uci);
  if (m === null) return null;
  const g = gainOf(m);
  make(pos, m);
  return g - search(pos, GIFT_SEARCH_PLIES, -2 * MATE, 2 * MATE, { nodes: 0 }, true);
}

/** The decisive score of `moveValue` (a mate either way is beyond ±this). */
export const MOVE_VALUE_MATE = MATE / 2;

/**
 * The material of `fen` once the captures on the board have settled (pawns, 1/3/3/5/9, the side to move's view): the
 * balance now plus what the side to move gains by captures alone (stand pat allowed; in check every evasion). For
 * tools that read the material at the end of an engine's line (tools/path-bank/authored.ts): a line cut in the middle
 * of an exchange is not a loss.
 */
export function settledMaterial(fen: string): number {
  const pos = parseFen(fen);
  let balance = 0;
  for (const p of pos.board) if (p && p.type !== 'k') balance += (p.color === pos.turn ? 1 : -1) * VALUE_PAWNS[p.type];
  return balance + search(pos, 0, -2 * MATE, 2 * MATE, { nodes: 0 }, true);
}

/**
 * All `isGift` captures of the side to move (UCI), in generation order (a1..h8 by origin square).
 * Capture-promotions are listed once, as the queen promotion.
 */
export function giftMoves(fen: string): string[] {
  const pos = parseFen(fen);
  return legalCaptures(pos)
    .filter((m) => (m.promo === null || m.promo === 'q') && isGiftMove(pos, m))
    .map(toUci);
}

/**
 * Squares the piece on `from` attacks («достаёт»): pure geometry — sliders stop at the first piece
 * (included), pawns reach diagonally only, pins and checks are ignored. With `targets: true` only the
 * squares occupied by opponent pieces. Empty `from` → [].
 */
export function reaches(fen: string, from: Square, opts?: { targets?: boolean }): Square[] {
  const idx = squareIndex(from);
  if (idx < 0) return [];
  const board = parsePlacement(fen);
  const piece = board[idx];
  if (!piece) return [];
  let squares = attacksFrom(board, idx);
  if (opts?.targets) squares = squares.filter((s) => board[s] && (board[s] as BoardPiece).color !== piece.color);
  return squares.sort((a, b) => a - b).map(squareName);
}

/**
 * Direct guards of the piece on `sq`: pieces of its own side that could legally recapture on `sq`.
 * Exactly: for every legal capture of it by the opponent (the opponent is put to move if needed),
 * the origins of the legal recaptures on `sq` — so a pinned defender, a king facing a covered square
 * or a recapture that would ignore a check are not guards, and a guard whose line was opened by the
 * capturer leaving it is. When the opponent cannot capture the piece at all, the static view is
 * used: direct defenders with absolutely pinned ones dropped, the king included only if the opponent
 * does not attack `sq`. X-ray guards behind another guard (queen behind rook) are NOT guards: they
 * become guards only after the front piece has recaptured. Sorted a1..h8; empty `sq` → [].
 */
export function guards(fen: string, sq: Square): Square[] {
  const target = squareIndex(sq);
  if (target < 0) return [];
  const base = parseFen(fen);
  const piece = base.board[target];
  if (!piece) return [];
  const pos = piece.color === base.turn ? flipped(base) : base;
  const out = new Set<number>();
  const captures = legalCaptures(pos).filter((m) => m.to === target);
  if (captures.length === 0) {
    const opp = opposite(piece.color);
    for (const d of defendersOf(base.board, target, piece.color)) {
      if ((base.board[d] as BoardPiece).type === 'k' && attackersOf(base.board, target, opp).length > 0) continue;
      out.add(d);
    }
  } else {
    for (const m of captures) {
      const u = make(pos, m);
      for (const from of legalCaptureFromsOn(pos, target)) out.add(from);
      unmake(pos, m, u);
    }
  }
  return [...out].sort((a, b) => a - b).map(squareName);
}

/**
 * The landing square of a capture that took at least what the capturing piece is worth (after a promotion: the new
 * piece), else -1. When the opponent then takes that piece as a gift, the exchange on that square is over and the
 * mover is not behind — an even or a winning trade («Бери, если забираешь не дешевле, чем отдаёшь», chapter 1.4), not
 * a dropped piece (review round 3: Bxc6 dxc6, Rxd5 exd5 for the queen, Nxd5 exd5 were all «ownHung»). `m` must be a
 * legal move of `pos`, not made yet.
 */
function tradedSquare(pos: Pos, m: Move): number {
  if (m.capSq < 0) return -1;
  const moving = pos.board[m.from] as BoardPiece;
  return gainOf(m) >= VALUE_PAWNS[m.promo ?? moving.type] ? m.to : -1;
}

/**
 * The square of the moved piece of `uci` when the move is a capture that took at least what that piece is worth (see
 * `tradedSquare`): losing it there afterwards is a trade, not a drop. Null for a quiet move, a capture of something
 * cheaper, or an illegal move. ONE rule for `moveGiftFacts.ownHung`, the drops of a path game (path/gameFacts.ts),
 * the own-game moments and riddles (path/own.ts) and `isSafeMove(…, { trades: true })`.
 */
export function tradedSquareOf(fenBefore: string, uci: string): Square | null {
  const pos = parseFen(fenBefore);
  const m = findMove(pos, uci);
  if (m === null) return null;
  const sq = tradedSquare(pos, m);
  return sq < 0 ? null : squareName(sq);
}

/**
 * Gift facts of a played move for MoveJudgement (§7.3), all from the mover's side:
 * - `giftAvailable`: before the move the mover had at least one `isGift` capture — not counted on a move that gives
 *   mate (a mate is better than any gift: the child who mates «missed» nothing, fixer r1);
 * - `giftTaken`: the move itself is an `isGift` capture;
 * - `ownHung`: after the move the opponent has an `isGift` capture of a mover piece worth >= 3
 *   (whether or not it was already hanging before; never after a mate) — except the piece that has just captured at
 *   least its own worth (`tradedSquareOf`: an even or a winning trade is no drop, review round 3).
 * Throws on an illegal move.
 */
export function moveGiftFacts(
  fenBefore: string,
  uci: string,
): { giftAvailable: boolean; giftTaken: boolean; ownHung: boolean } {
  const pos = parseFen(fenBefore);
  const m = findMove(pos, uci);
  if (!m) throw new Error(`moveGiftFacts: illegal move ${uci} in ${fenBefore}`);
  const giftTaken = isGiftMove(pos, m);
  const anyGift = giftTaken || legalCaptures(pos).some((c) => isGiftMove(pos, c));
  const traded = tradedSquare(pos, m);
  make(pos, m);
  const mate = inCheck(pos) && legalMoves(pos).length === 0;
  const giftAvailable = giftTaken || (anyGift && !mate);
  let ownHung = false;
  if (!mate) {
    const hung = giftTargets(pos, OWN_HUNG_MIN);
    hung.delete(traded);
    ownHung = hung.size > 0;
  }
  return { giftAvailable, giftTaken, ownHung };
}

/**
 * The pieces the side to move can take as a gift (`isGift`), with what they are: square → piece type (en passant: the
 * passed pawn's square). `minValue` in pawns (1/3/3/5/9). For counting gift CHANCES per gift, not per ply (core
 * path/gameFacts.ts): a gift that stays on the board for five moves is one chance.
 */
export function giftTargetsOf(fen: string, minValue = 1): Map<Square, PieceType> {
  const pos = parseFen(fen);
  const out = new Map<Square, PieceType>();
  for (const sq of [...giftTargets(pos, minValue)].sort((a, b) => a - b)) {
    const p = pos.board[sq];
    if (p) out.set(squareName(sq), p.type);
  }
  return out;
}

/**
 * Is the move safe ("Этот ход безопасен?"): after it the opponent has no `isGift` capture of a mover
 * piece worth >= 1 that was not already a gift before the move.
 * - "Already a gift before" = the opponent, put to move in the position before the move (en passant
 *   cleared), had an `isGift` capture of that piece on that square. If the mover was in check before
 *   the move this view does not exist and nothing counts as already given.
 * - A piece the move put on a new square (the moved piece, a promoted piece, the castling rook) is
 *   always judged afresh: leaving the moved piece en prise is never safe.
 * - Only gifts count: a piece taken in a losing exchange (a recapture exists) is not "unsafe" here.
 * - `trades: true` (the child's own games — the moments and riddles of path/own.ts, review round 3): the moved piece of
 *   a capture that took at least its own worth (`tradedSquareOf`) does not count either — an even trade is no gift.
 *   The chapters' rule (1.3 «Безопасно?», the generator, the bank) stays without it: their moves are quiet.
 * Throws on an illegal move.
 */
export function isSafeMove(fen: string, uci: string, opts: { trades?: boolean } = {}): boolean {
  const pos = parseFen(fen);
  const m = findMove(pos, uci);
  if (!m) throw new Error(`isSafeMove: illegal move ${uci} in ${fen}`);
  const before = clonePos(pos);
  const mover = pos.turn;
  const traded = opts.trades === true ? tradedSquare(pos, m) : -1;
  make(pos, m);
  const after = giftTargets(pos, 1);
  after.delete(traded);
  if (after.size === 0) return true;
  const already = inCheck(before) ? new Set<number>() : giftTargets(flipped(before), 1);
  for (const sq of after) {
    const now = pos.board[sq] as BoardPiece;
    const was = before.board[sq];
    const sameSquareSamePiece = was === now && now.color === mover;
    if (!sameSquareSamePiece || !already.has(sq)) return false;
  }
  return true;
}

/** @internal Test hook: every legal move of the side to move (UCI), as this module generates them. */
export function legalMovesUciForTest(fen: string): string[] {
  return legalMoves(parseFen(fen)).map(toUci);
}
